import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  ViewChild,
  OnChanges,
  SimpleChanges,
  computed,
} from '@angular/core';
import { LazyLoadEvent, SortEvent } from 'primeng/api';
import { Table, TableModule } from 'primeng/table';
import { TravelBoardService } from '../../services/travel-board.service';
import { TravelBoardData } from 'src/app/shared/model/travel-board-data';
import { CompanyClientService } from 'src/app/shared/services/company-client.service';
import { PackLimitationsService } from 'src/app/shared/services/pack-limitations.service';
import { TableSettingsService } from '../../services/table-settings.service';
import { TranslocoPipe } from '@jsverse/transloco';
import { MultiSelectModule } from 'primeng/multiselect';
import { FormsModule } from '@angular/forms';
import { ClientAvatarComponent } from '../client-avatar/client-avatar.component';
import { CommonModule } from '@angular/common';
import { TooltipModule } from 'primeng/tooltip';
import { FormatCO2ePipe } from 'src/app/pipes/format-co2e.pipe';
import { ActionsComponent } from 'src/app/shared/actions/actions.component';
import { LucideAngularModule } from 'lucide-angular';
import { EcoscoreComponent } from '../ecoscore/ecoscore.component';

@Component({
  selector: 'app-table-view',
  imports: [
    TableModule,
    TranslocoPipe,
    MultiSelectModule,
    FormsModule,
    ClientAvatarComponent,
    CommonModule,
    TooltipModule,
    FormatCO2ePipe,
    EcoscoreComponent,
    ActionsComponent,
    LucideAngularModule,
  ],
  templateUrl: './table-view.component.html',
  styleUrl: './table-view.component.scss',
})
export class TableViewComponent implements OnInit, OnChanges {
  trips: TravelBoardData[] = [];
  travelBoardService = inject(TravelBoardService);
  private companyClientService = inject(CompanyClientService);
  packLimitationService = inject(PackLimitationsService);
  tableSettingService = inject(TableSettingsService);
  @ViewChild('dt') dt: Table | undefined;

  selectedItems: TravelBoardData[] = [];
  @Output() selectionChange = new EventEmitter<TravelBoardData[]>();
  @Output() pageChange = new EventEmitter<{ page: number; rows: number }>();

  clientNameFilterOptions = computed(() =>
    this.companyClientService.companyClients().map((client) => client.name),
  );
  clientNameFilterValue: string[] = [];

  private readonly ROWS_STORAGE_KEY = 'travelBoard.tableView.rows';
  private readonly SORT_STORAGE_KEY = 'travelBoard.tableView.sort';

  first = 0;
  rows = parseInt(localStorage.getItem(this.ROWS_STORAGE_KEY) || '5', 10);

  sortField: string = '';
  sortOrder: number = 1;

  @Input() filteredTrips: TravelBoardData[] = [];

  constructor() {
    this.loadSortFromStorage();
  }

  ngOnInit(): void {
    // Initialize page from service (which reads from URL query params)
    // Keep rows from localStorage (user preference)
    const initialPage = this.travelBoardService.paginationPage();
    this.first = initialPage * this.rows;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['filteredTrips']) {
      this.trips = this.filteredTrips;
    }
  }

  private loadSortFromStorage() {
    const savedSort = localStorage.getItem(this.SORT_STORAGE_KEY);
    if (savedSort) {
      try {
        const { field, order } = JSON.parse(savedSort);
        this.sortField = field || '';
        this.sortOrder = order ?? 1;
      } catch {
        // Invalid JSON, use defaults
      }
    }
  }

  private saveSortToStorage(field: string, order: number) {
    localStorage.setItem(
      this.SORT_STORAGE_KEY,
      JSON.stringify({ field, order }),
    );
  }

  onSelectionChange() {
    this.selectionChange.emit(this.selectedItems);
  }

  /**
   * FILTER FUNCTIONS
   */

  filterByClient() {
    if (this.clientNameFilterValue.length > 0) {
      this.trips = this.filteredTrips.filter((trip) =>
        trip.companyClients?.some((client) =>
          this.clientNameFilterValue.includes(client),
        ),
      );
    } else {
      this.trips = this.filteredTrips;
    }
  }

  customSort(event: SortEvent) {
    // Save sort state
    if (event.field) {
      this.saveSortToStorage(event.field, event.order ?? 1);
    }

    event.data?.sort((data1: TravelBoardData, data2: TravelBoardData) => {
      let value1: string | number | undefined;
      let value2: string | number | undefined;

      if (event.field === 'countryName') {
        value1 = data1.countryName;
        value2 = data2.countryName;
      } else if (event.field === 'clientName') {
        value1 = data1.clientName;
        value2 = data2.clientName;
      } else if (event.field === 'totalImpact') {
        value1 = data1.totalImpact;
        value2 = data2.totalImpact;
      } else if (event.field === 'ImpactPerTraveller') {
        value1 = data1.impactPerTraveller;
        value2 = data1.impactPerTraveller;
      } else {
        value1 = data1[event.field as keyof TravelBoardData] as
          | string
          | number
          | undefined;
        value2 = data2[event.field as keyof TravelBoardData] as
          | string
          | number
          | undefined;
      }

      let result: number | null = null;

      if (value1 == null && value2 != null) result = -1;
      else if (value1 != null && value2 == null) result = 1;
      else if (value1 == null && value2 == null) result = 0;
      else if (typeof value1 === 'string' && typeof value2 === 'string')
        result = value1.localeCompare(value2);
      else if (
        value1 !== undefined &&
        value1 !== null &&
        value2 !== undefined &&
        value2 !== null
      )
        result = value1 < value2 ? -1 : value1 > value2 ? 1 : 0;
      else result = 0;

      return (event.order ?? 0) * result;
    });
  }
  clear(table: Table, input: HTMLInputElement) {
    table.clear();
    input.value = '';
  }

  /**
   * PAGINATION FUNCTIONS
   */
  next() {
    this.first = this.first + this.rows;
  }

  prev() {
    this.first = Math.max(this.first - this.rows, 0);
  }

  reset() {
    this.first = 0;
  }

  onPageChange(event: LazyLoadEvent) {
    this.first = event.first ?? 0;
    this.rows = event.rows ?? 5;
    localStorage.setItem(this.ROWS_STORAGE_KEY, this.rows.toString());

    // Emit to parent for URL update
    const pageIndex = Math.floor(this.first / this.rows);
    this.pageChange.emit({ page: pageIndex, rows: this.rows });
  }

  isLastPage(): boolean {
    return this.first + this.rows >= this.trips.length;
  }

  isFirstPage(): boolean {
    return this.first === 0;
  }
}
